import { Box } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import type { DictDefinition, LinkDefinition, ListDefinition } from '@deity-io/di-data-structure-editor';
import { FieldDefinition } from '@deity-io/di-data-structure-editor';
import { Collapsible } from 'components/PropertyListEditor/Collapsible';
import PropertyListEditor from 'components/PropertyListEditor/PropertyListEditor';
import { SchemaActionTypes, SchemaContext } from 'contexts/SchemaContext';
import _ from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';

interface EditFieldDefaultProps {
  locked: boolean;
  path: Array<string>;
  schema: DictDefinition | ListDefinition | LinkDefinition | FieldDefinition;
  link?: LinkDefinition;
}
function normalizeTypeConfig(typeConfig: any): IPropertyBag {
  if (typeConfig && typeConfig.type && typeof typeConfig.type === 'object') {
    return typeConfig.type[Object.keys(typeConfig.type)[0]];
  }

  return {};
}

const EditFieldDefaults: FC<EditFieldDefaultProps> = ({ locked, path, schema, link }) => {
  const ctx = useContext(SchemaContext);
  const fieldProperties = link ? schema.getProperties(undefined, schema.config) : schema.getProperties();
  const schemaTypeConfig = normalizeTypeConfig(schema.config);
  const linkTypeConfig = normalizeTypeConfig(link?.config);

  // eslint-disable-next-line no-nested-ternary
  const typeProperties = FieldDefinition.isItMe(schema)
    ? link
      ? schema.getTypeProperties(undefined, schemaTypeConfig)
      : schema.getTypeProperties()
    : {};

  const typeConfig = link ? { ...schemaTypeConfig, ...linkTypeConfig } : schemaTypeConfig;
  const typeTag = link ? link.getTypeTag() : schema.getTypeTag();

  const overriddenTypeProperties = mapRecord(typeProperties, (property, key) => {
    if (typeof schema.config.type === 'object' && key in schema.config.type[Object.keys(schema.config.type)[0]]) {
      return property;
    }

    return undefined;
  });

  return (
    <Box>
      {!_.isEmpty(fieldProperties) && (
        <Collapsible
          title="Field Settings"
          propertyBag={link?.config || schema.config}
          propertyList={fieldProperties}
          itemTitle="PROPERTIES"
        >
          <PropertyListEditor
            propertyList={fieldProperties}
            data={link?.config || schema.config}
            owner={schema}
            locked={ctx?.state.locked || locked}
            onChange={(key, value) => {
              ctx?.dispatch({
                type: SchemaActionTypes.UPDATE_SETTING,
                payload: { path, property: key, value }
              });
            }}
            onDelete={key => {
              ctx?.dispatch({
                type: SchemaActionTypes.UPDATE_SETTING,
                payload: { path, property: key, value: undefined }
              });
            }}
          />
        </Collapsible>
      )}
      {!_.isEmpty(typeProperties) && (
        <Collapsible
          title="Type Settings"
          propertyBag={overriddenTypeProperties}
          propertyList={typeProperties}
          itemTitle="PROPERTIES"
        >
          <PropertyListEditor
            propertyList={typeProperties}
            data={typeConfig}
            owner={schema}
            locked={ctx?.state.locked || locked}
            onChange={(key, value) => {
              ctx?.dispatch({
                type: SchemaActionTypes.UPDATE_SETTING,
                payload: {
                  path,
                  property: ['type', typeTag, ...key],
                  value
                }
              });
            }}
            onDelete={key => {
              ctx?.dispatch({
                type: SchemaActionTypes.UPDATE_SETTING,
                payload: {
                  path,
                  property: ['type', typeTag, ...key],
                  value: undefined
                }
              });
            }}
          />
        </Collapsible>
      )}
    </Box>
  );
};

export default EditFieldDefaults;
